import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CardContent,
  Rating,
  Chip,
  Grid,
  IconButton,
  Link,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Language as WebsiteIcon,
  Star as StarIcon,
  AttachMoney as PriceIcon,
  OpenInNew as ExternalLinkIcon,
  Map as MapIcon,
  Directions as DirectionsIcon,
  AccessTime as AccessTimeIcon,
  People as PeopleIcon,
  Wifi as WifiIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Reviews as ReviewsIcon,
} from '@mui/icons-material';

const ItemPreviewModal = ({ open, onClose, item, category, onSelect }) => {
  const [activeTab, setActiveTab] = useState(0);
  
  // Generate Google Maps link for the location
  const getGoogleMapsLink = (item) => {
    if (!item || !item.name) return '#';
    
    const searchQuery = encodeURIComponent(`${item.name} ${item.city || ''}`.trim());
    return `https://www.google.com/maps/search/?api=1&query=${searchQuery}`;
  };
  
  if (!item) return null;

  const getCategoryColor = (category) => {
    const colors = {
      locations: '#4caf50',
      restaurants: '#ff9800', 
      activities: '#2196f3',
      hotels: '#9c27b0'
    };
    return colors[category] || '#666';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      locations: '🏛️',
      restaurants: '🍽️',
      activities: '🎯',
      hotels: '🏨'
    };
    return icons[category] || '📍';
  };

  const handleExternalLink = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {getCategoryIcon(category)} {item.name}
          </Typography>
          <Chip 
            label={category?.charAt(0).toUpperCase() + category?.slice(1)}
            size="small"
            sx={{ 
              backgroundColor: getCategoryColor(category),
              color: 'white',
              fontWeight: 500
            }}
          />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Google Maps Link Section */}
        {item && (
          <Box sx={{ 
            p: 3, 
            backgroundColor: '#f8f9fa', 
            borderBottom: '1px solid #e0e0e0',
            textAlign: 'center'
          }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {getCategoryIcon(category)} {item.name}
            </Typography>
            
            <Button
              variant="contained"
              startIcon={<MapIcon />}
              endIcon={<ExternalLinkIcon />}
              href={getGoogleMapsLink(item)}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                backgroundColor: '#4285f4',
                '&:hover': {
                  backgroundColor: '#3367d6'
                },
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              View on Google Maps
            </Button>
            
            {item.city && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                📍 {item.city}
              </Typography>
            )}
          </Box>
        )}

        <CardContent sx={{ p: 0 }}>
          {/* Tabs for different sections */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="fullWidth"
            >
              <Tab label="Overview" icon={<InfoIcon />} />
              <Tab label="Details" icon={<ReviewsIcon />} />
              <Tab label="Location" icon={<MapIcon />} />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ p: 3 }}>
            {/* Overview Tab */}
            {activeTab === 0 && (
              <Box>
                {/* Quick Stats */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {item.rating && (
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <StarIcon sx={{ color: '#ffc107', fontSize: 24, mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {item.rating}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Rating
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                  
                  {item.priceRange && (
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <PriceIcon sx={{ color: '#4caf50', fontSize: 24, mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {item.priceRange}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Price Range
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {item.duration && (
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <AccessTimeIcon sx={{ color: '#2196f3', fontSize: 24, mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {item.duration}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Duration
                        </Typography>
                      </Paper>
                    </Grid>
                  )}

                  {item.capacity && (
                    <Grid item xs={6} sm={3}>
                      <Paper sx={{ p: 2, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                        <PeopleIcon sx={{ color: '#9c27b0', fontSize: 24, mb: 1 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {item.capacity}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Capacity
                        </Typography>
                      </Paper>
                    </Grid>
                  )}
                </Grid>

                {/* Description */}
                {item.description && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                      <InfoIcon sx={{ mr: 1, color: getCategoryColor(category) }} />
                      About
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {item.description}
                    </Typography>
                  </Box>
                )}

                {/* Why Recommended */}
                {item.whyRecommended && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                      <StarIcon sx={{ mr: 1, color: '#ffc107' }} />
                      Why We Recommend This
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {item.whyRecommended}
                    </Typography>
                  </Box>
                )}

                {/* Quick Actions */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
                  {item.googleMapsUrl && (
                    <Button
                      variant="contained"
                      startIcon={<DirectionsIcon />}
                      onClick={() => handleExternalLink(item.googleMapsUrl)}
                      sx={{ 
                        backgroundColor: '#4caf50',
                        '&:hover': { backgroundColor: '#45a049' }
                      }}
                    >
                      Get Directions
                    </Button>
                  )}
                  
                  {item.website && (
                    <Button
                      variant="outlined"
                      startIcon={<WebsiteIcon />}
                      endIcon={<ExternalLinkIcon />}
                      onClick={() => handleExternalLink(item.website)}
                      sx={{ 
                        borderColor: getCategoryColor(category),
                        color: getCategoryColor(category),
                        '&:hover': {
                          backgroundColor: getCategoryColor(category) + '10',
                          borderColor: getCategoryColor(category)
                        }
                      }}
                    >
                      Visit Website
                    </Button>
                  )}

                  {item.bookingUrl && (
                    <Button
                      variant="contained"
                      startIcon={<StarIcon />}
                      onClick={() => handleExternalLink(item.bookingUrl)}
                      sx={{ 
                        backgroundColor: '#ff9800',
                        '&:hover': { backgroundColor: '#f57c00' }
                      }}
                    >
                      Book Now
                    </Button>
                  )}
                </Box>
              </Box>
            )}

            {/* Details Tab */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                  Detailed Information
                </Typography>

                {/* Contact Information */}
                {(item.phone || item.email || item.website) && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon sx={{ mr: 1, color: getCategoryColor(category) }} />
                        Contact Information
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <List>
                        {item.phone && (
                          <ListItem>
                            <ListItemIcon><PhoneIcon /></ListItemIcon>
                            <ListItemText primary="Phone" secondary={item.phone} />
                          </ListItem>
                        )}
                        {item.email && (
                          <ListItem>
                            <ListItemIcon><WebsiteIcon /></ListItemIcon>
                            <ListItemText primary="Email" secondary={item.email} />
                          </ListItem>
                        )}
                        {item.website && (
                          <ListItem>
                            <ListItemIcon><WebsiteIcon /></ListItemIcon>
                            <ListItemText 
                              primary="Website" 
                              secondary={
                                <Link href={item.website} target="_blank" rel="noopener">
                                  {item.website}
                                </Link>
                              } 
                            />
                          </ListItem>
                        )}
                      </List>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Amenities & Features */}
                {item.amenities && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <WifiIcon sx={{ mr: 1, color: getCategoryColor(category) }} />
                        Amenities & Features
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={1}>
                        {item.amenities.map((amenity, index) => (
                          <Grid item xs={6} sm={4} key={index}>
                            <Chip 
                              label={amenity} 
                              size="small" 
                              sx={{ mb: 1, width: '100%' }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Operating Hours */}
                {item.hours && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon sx={{ mr: 1, color: getCategoryColor(category) }} />
                        Operating Hours
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {item.hours}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Reviews & Ratings */}
                {item.reviews && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                        <ReviewsIcon sx={{ mr: 1, color: getCategoryColor(category) }} />
                        Reviews & Ratings
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box>
                        {item.rating && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Rating value={parseFloat(item.rating)} readOnly size="large" />
                            <Typography variant="h6" sx={{ ml: 2 }}>
                              {item.rating} out of 5
                            </Typography>
                          </Box>
                        )}
                        {item.reviews.map((review, index) => (
                          <Box key={index} sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                              "{review.text}"
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              - {review.author}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            )}

            {/* Location Tab */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                  <MapIcon sx={{ mr: 1, color: getCategoryColor(category) }} />
                  Location & Directions
                </Typography>

                {/* Address */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Address
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {item.address || item.location || 'Address not available'}
                  </Typography>
                  
                  {item.googleMapsUrl && (
                    <Button
                      variant="contained"
                      startIcon={<DirectionsIcon />}
                      onClick={() => handleExternalLink(item.googleMapsUrl)}
                      sx={{ 
                        backgroundColor: '#4caf50',
                        '&:hover': { backgroundColor: '#45a049' }
                      }}
                    >
                      Open in Google Maps
                    </Button>
                  )}
                </Box>

                {/* Nearby Attractions */}
                {item.nearbyAttractions && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Nearby Attractions
                    </Typography>
                    <List>
                      {item.nearbyAttractions.map((attraction, index) => (
                        <ListItem key={index}>
                          <ListItemIcon><LocationIcon /></ListItemIcon>
                          <ListItemText 
                            primary={attraction.name}
                            secondary={`${attraction.distance} away`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Transportation */}
                {item.transportation && (
                  <Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Getting There
                    </Typography>
                    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                      {item.transportation}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

          </Box>
        </CardContent>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} variant="outlined">
          Close Preview
        </Button>
        <Button 
          onClick={onSelect || onClose} 
          variant="contained"
          sx={{ 
            backgroundColor: getCategoryColor(category),
            '&:hover': {
              backgroundColor: getCategoryColor(category),
              opacity: 0.9
            }
          }}
        >
          Select This Item
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemPreviewModal;
